<template>
  <div class="predicted-category-section">
    <div class="section-row" v-bind:class="{ 'disabled-row': disabled, 'show-separate-line': showSeparateLine }">
      <div class="label-wrapper">
        <div class="week-indication" :class="{ 'cashflow-type-background-color': isCurrentWeek }"></div>
        <div class="label" :class="{ 'clickable': hasTransactions }" v-on:[toggleCategoryClickEvent]="toggleCategory">
          <span class="fa" v-if="hasTransactions" :class="{ 'fa-angle-left': !expanded, 'fa-angle-down': expanded}"/>
          <span>{{ shortLabel || label }}</span>
        </div>
      </div>
      <div class="actuals cashflow-type-color">
        <decimal-number :value="actualsSum" />
        <span> ₪</span>
      </div>
      <div v-if="showRecommendedAmount" class="recommended" v-on:[editAmountClickEvent]="editAmountClicked">
        <span class="pencil-wrapper">
          <img class="pencil" v-if="canEditCategory" :src="require('@/assets/pencil.svg')">
        </span>
        <span class="recommended-amount" :class="{ 'clickable editable-number': canEditCategory }">
          <animated-decimal-number
            v-bind:value="recommendedAmount"/>
          <span> ₪</span>
        </span>
      </div>
    </div>
    <slot name="transactions" v-if="expanded" :transactions="transactions"/>
  </div>
</template>

<script>
import TransactionDisplayUtils from '../../TransactionDisplayUtils.vue';
import AnimatedDecimalNumber from '../../../../../../../../../base-components/AnimatedDecimalNumber.vue';
import DecimalNumber from '../../../../../../../../../base-components/DecimalNumber.vue';
import Segment from '../../../../../../../../../Segment';
import constants from '../../../../../../../../../constants/cashflow-view';
import EventBus from '../../../../../../../../../event-bus/event-bus';
import effectsUtils from '../../../../../../../../../store/utilities/effects';
import DDLogs from '../../../../../../../../../DDLogs';

export default {
  name: 'PredictedCategorySection',
  components: {
    AnimatedDecimalNumber,
    DecimalNumber,
  },
  mixins: [TransactionDisplayUtils],
  props: [
    'disabled',
    'label',
    'shortLabel',
    'showSeparateLine',
    'initiallyExpanded',
    'actualsSum',
    'showRecommendedAmount',
    'recommendedAmount',
    'transactions',
    'canEditCategory',
    'categoryId',
    'timeFrameDetails',
  ],
  data() {
    return {
      expanded: this.initiallyExpanded,
    };
  },
  created() {
    EventBus.$on('expand-category', async ({ categoryToExpand, transactionId, envelopeId }) => {
      if (envelopeId) {
        this.expandToEnvelope(envelopeId);
      }
      if (transactionId) {
        this.expandToTransaction(transactionId);
      }
    });
  },
  computed: {
    hasTransactions() {
      return this.transactions.length > 0;
    },
    isCurrentWeek() {
      return this.timeFrameDetails
        && this.timeFrameDetails.timeFrame === constants.envelopeTimeFrames.weekly
        && this.timeFrameDetails.isCurrentWeek;
    },
    toggleCategoryClickEvent() {
      return (this.disabled || !this.hasTransactions) ? null : 'click';
    },
    editAmountClickEvent() {
      return this.canEditCategory ? 'click' : null;
    },
  },
  methods: {
    toggleCategory() {
      this.expanded = !this.expanded;
      this.$emit('section-clicked', { section: this.label, expanded: this.expanded });
    },
    editAmountClicked() {
      Segment.trackUserInteraction('EditAmountClicked', {
        expenseCategory: this.expenseCategory,
        categoryId: this.categoryId,
      });
      this.$emit('edit-amount-clicked', { categoryId: this.categoryId });
    },
    isEnvelope(transaction) {
      return _.has(transaction, 'envelopeId');
    },
    scrollToAndHighlighElement(elementId) {
      effectsUtils.scrollToAndHighlighElement(elementId);
    },
    expandToTransaction(transactionId) {
      DDLogs.log('Section: scrolling to transaction', { transactionId, category: this.label });
      _.forEach(this.transactions, transaction => {
        const currentTransactionId = this.isEnvelope(transaction)
          ? transaction.actual && transaction.actual.transactionId
          : transaction.transactionId;
        if (currentTransactionId === transactionId) {
          DDLogs.log('Section: found transaction. Expanding section', { transactionId, category: this.label });
          this.expanded = true;
          const elementId = this.isEnvelope(transaction)
            ? transaction.envelopeId
            : transaction.transactionId;
          DDLogs.log('Section: scrolling to element', { elementId, category: this.label });
          this.scrollToAndHighlighElement(elementId);
        }
      });
    },
    expandToEnvelope(envelopeId) {
      if (this.isEnvelope(this.transactions[0])) {
        _.forEach(this.transactions, envelope => {
          if (envelope.envelopeId === envelopeId) {
            this.expanded = true;
            this.scrollToAndHighlighElement(envelopeId);
          }
        });
      }
    },
  },
};
</script>

<style lang="scss">
  @import '../../../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';

  .predicted-category-section {
    .section-row {
      display: flex;
      height: 40px;
      line-height: 40px;
      font-size: 14px;
      border-top: 1px solid $riseup_gray_0;

      .clickable {
        cursor: pointer;
      }

      .fa {
        text-align: center;
      }

      .actuals {
        width: 78px;
        overflow: hidden;
        white-space: nowrap;
        font-weight: bold;

        .decimal {
          font-size: 10px;
        }
      }

      .recommended {
        &.clickable {
          cursor: pointer;
        }

        .recommended-amount {
          width: 88px;
          .animated-decimal-number {
            .decimal {
              font-size: 10px;
            }
          }
        }

        .pencil-wrapper {
          width: 8px;
          display: inline-block;

          .pencil {
            height: 8px;
            cursor: pointer;
          }
        }
      }

      .label-wrapper {
        display: flex;
        flex-direction: row;

        .week-indication {
          width: 4px;
          height: 100%;
        }

        .label {
          padding-right: 8px;
          width: 100px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      &.disabled-row {
        div {
          color: $riseup_gray_1;
        }
        .recommended {
          .recommended-amount {
            &.editable-number {
              color: $riseup_black;
            }
          }
        }
      }

      &.show-separate-line {
        border-top: 1px solid $riseup_gray_3;
      }
    }
  }
</style>
